import { CSSProperties, ForwardedRef, forwardRef, useState } from 'react';
import styles from './SimpleFile.module.scss';
import DownloadButton from '../shared/DownloadButton';
import { ServiceDownload } from '../../services/service-download';
import { FileScope } from '../../util/appEnum';
import { ScopeOptions } from '../../models/global.model';
import _ from 'lodash';
import { ReactComponent as FileIcon } from '../../assets/svg/file.svg';
import { ReactComponent as EditIcon } from '../../assets/svg/editIcon.svg';
import { TrashIcon } from '../svgs';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Spinner from '../shared/Spinner/Spinner';
import MiddleEllipsis from 'react-middle-ellipsis';

type Props = {
    fileName: string,
    disabled?: boolean,
    file?: string,
    url?: string,
    style?: CSSProperties,
    scope?: FileScope,
    scopeOptions?: ScopeOptions,
    comment?: string,
    submittedDate?: Date,
    validUntilDate?: Date,
    blob?: Blob,
    className?: string,
    canEdit?: boolean,
    onRemove?: () => void,
    uploadedBy?: string,
    noFile?: boolean,
    noFileNameTitle?: string,
    onEdit?: () => void,
}

const SimpleFile = forwardRef(function SimpleFile({
    style,
    fileName = '',
    disabled,
    file,
    url,
    scope,
    scopeOptions,
    comment,
    submittedDate,
    validUntilDate,
    blob,
    className,
    canEdit = false,
    onRemove,
    uploadedBy,
    noFile,
    noFileNameTitle,
    onEdit,
}: Props, ref: ForwardedRef<HTMLDivElement>) {

    const getFilename = (file: string) => {
        return file?.slice(((file.lastIndexOf('/') - 1) >>> 0) + 2) || file;
    }
    const [t] = useTranslation('supplierDatabase', { keyPrefix: 'supplierDetailPanel' });
    const [isHovered, setIsHovered] = useState(false);

    let active = false;
    let end;
    const pickedDate = validUntilDate;
    if (pickedDate) {
        end = new Date(pickedDate);
        end.setHours(23, 59, 59, 999);
        active = new Date() < end;
    }
    const activeLabel = t('liabilityInsurance.activeLabel', 'Aktiv')
    const inactiveLabel = t('liabilityInsurance.inactiveLabel', 'Utgången')
    const expiresLabel = active ? t('liabilityInsurance.expiresLabel', 'Giltig till') : t('frameworkAgreement.invalidSinceLabel', 'Ogiltig sedan')
    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onRemove && window.confirm(t('confirm.removeFile', 'Är du säker på att du vill ta bort denna fil?'))) {
            onRemove();
        }
    }
    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onEdit) onEdit();
    }

    const uploadedByRow = uploadedBy ? <span>{t('added.by', 'Tillagt av {{uploaded}}', { uploaded: uploadedBy }) + ' ' + moment(submittedDate).format('YYYY-MM-DD HH:mm')}</span> : null;
    const commentRow = comment ? <span>{comment}</span> : null;
    const validUntilDateRow = validUntilDate && end ? <span>{expiresLabel + " " + moment(end).format('YYYY-MM-DD')}</span> : null;
    const submittedDateRow = submittedDate ? <span>{t('submitted.on', 'Inlämnad den') + " " + moment(submittedDate).format('YYYY-MM-DD')}</span> : null;
    // Ensure file is a string before passing to getFileDownloadURL
    const fileUrl = url || (file && typeof file === 'string' && !_.isEmpty(file) && scope && !_.isEmpty(scopeOptions) && ServiceDownload.getFileDownloadURL(file, scope, scopeOptions)) || '';

    return (
        <DownloadButton
            disabled={disabled}
            url={fileUrl}
            blob={blob}
            fileName={fileName}
            render={({ downloading, progress, onClick }) => {
                const hasFile = file && fileName;
                const showFullDetails = !noFile;
                const hasMetadata = comment || validUntilDate || submittedDate;

                return (
                    <div
                        ref={ref}
                        className={`${className} ${styles.container} ${disabled ? styles.disabled : ''} ${(!file && !hasMetadata) ? styles['container--no-file'] : ''} ${!hasFile && hasMetadata ? styles['container--no-file-file'] : ''}`}
                        style={style}
                        onClick={hasFile ? onClick : undefined}
                        key={fileName + (validUntilDate || submittedDate)}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <div className={styles['container__file-holder']}>
                            {(hasFile || hasMetadata) && (
                                <div className={styles['container__file-icon']}>
                                    <FileIcon />
                                </div>
                            )}
                            <div className={styles['container__name-comment']}>
                                <div className={`${styles['container__name']} ${(!hasFile && !hasMetadata) ? styles['container__name--no-file'] : ''}`}>
                                    {hasFile ? <MiddleEllipsis>
                                        <span className="ellipseMe">{getFilename(fileName)}</span>
                                    </MiddleEllipsis> : hasMetadata ? <MiddleEllipsis>
                                        <span className="ellipseMe">{noFileNameTitle}</span>
                                    </MiddleEllipsis> : null}
                                </div>
                                {showFullDetails && (<>
                                    <div className={styles['container__comment']}>
                                        {uploadedByRow}
                                    </div>
                                    <div className={styles['container__comment']}>
                                        {validUntilDateRow}
                                    </div>
                                    <div className={styles['container__comment']}>
                                        {submittedDateRow}
                                    </div>
                                    <div className={styles['container__comment']}>
                                        {commentRow}
                                    </div>
                                </>

                                )}
                            </div>
                        </div>

                        {showFullDetails && downloading && (
                            <Spinner isHidden={!downloading} size='xxs' progress={progress} />
                        )}

                        {isHovered && canEdit ?
                            <div className={styles['container__actions-holder']}>
                                {onEdit ? <div className={styles['container__edit-icon']} onClick={handleEdit}><EditIcon /></div> : <div style={{ width: '18px' }}></div>}
                                <div className={styles['container__remove-icon']} onClick={handleRemove} > <TrashIcon /> </div>
                            </div> :
                            (validUntilDate ? (
                                <div className={`${styles['container__status']} ${!active ? styles['container__status--inactive'] : ''}`}>
                                    <span>{active ? activeLabel : inactiveLabel}</span>
                                </div>
                            ) : <div style={{ width: '65px' }}></div>)}
                    </div>
                );
            }}
        />
    )
})

export default SimpleFile;
